import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '../../../../components/Button';
import Storage from '../../../../utils/Storage';
import { setGenptSubscribeModal } from '../../../../redux/reducers/dashboard';
import SiembaGPTIconGrey from '../../../../components/SvgIcons/GPTIconGrey';
import SiembaGPTIconTheme from '../../../../components/SvgIcons/GPTIconTheme';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Modal from '../../../../components/MuiComponents/Modal';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Typography from '../../../../components/Typography';
import MainLoader from '../../../../components/Loader/MainLoader';
import { useQueryClient } from 'react-query';
import {
  useCreateGenPTSchedule,
  useUpdateGenPTSchedule,
  useGenptSchedulesList,
  getGenptSchedulesTotalCount,
  GENPT_SCHEDULES_ACTIVE_TOTAL_QUERY_KEY,
  useValidateTarget,
} from '../../../../hooks/siembagpt';
import { AlertContext } from '../../../../components/SuccessOrFailedModal';
import { useValidateLogin } from '../../../../hooks/auth';
import { useAppMode } from '../../../../Providers/AppModeProvider';
import { LayoutContext } from '../../../../Providers/LayoutProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import pluralize from 'pluralize';
import TestConfigurationForm from '../Chat/components/TestConfigurationForm';
import APIConfigurationForm from '../Chat/components/APIConfigurationForm';
import buildGenptScheduleApiPayload, {
  buildGenptScheduleCreateBody,
  buildGenptScheduleUpdatePutBody,
  pickChangedSchedulePutFields,
} from '../Chat/utils/buildGenptSchedulePayload';
import mapGenptScheduleToEditPrefill from '../utils/mapGenptScheduleToEditPrefill';
import { hasGenptScheduleSlotConflict } from '../utils/genptScheduleTimeConflictCheck';
import mapPipelineConfigToGenptEditPrefill from '../utils/mapPipelineConfigToGenptEditPrefill';
import { API, WEB_APP } from '../../../../utils/constants/assetTypeRegistry';
import ScheduleGenPTAssetStep from './ScheduleGenPTAssetStep';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { platformMapping } from '../../../../actions/commonActions';
import GreenCheck from '../../../../components/SvgIcons/GreenCheck';
import FeatureLockIcon from '../../../../components/Billing/FeatureLockIcon';
import { FEATURE_KEYS, isFeatureLocked } from '../../../../utils/constants/featureAccess';

const TARGET_ASSET_ICON_SRC = '/images/genpt/target_asset.svg';

const MODAL_DESCRIPTION =
  'Schedule a GenPT test by setting up your configuration, setting throttling limits, and choosing when the test should run. Make sure required credentials are valid and active.';

const EDIT_MODAL_DESCRIPTION =
  'Review and update your configuration or schedule. Existing configurations remain intact unless changed.';

const ASSET_STEP_DESCRIPTION =
  'Schedule a GenPT test by selecting a target asset first. Then configure the test, define throttling limits, schedule execution, and ensure required credentials are valid and active.';

/**
 * ROAR-4316: When the asset already has prior GenPT test history (validate_target returns
 * `latest_session`), the modal scaffolds the new schedule from that latest configuration —
 * so the header should reflect "review existing setup" instead of "set everything up". Active
 * schedules alone (no test history) keep the default copy; the gray "N active schedules"
 * banner already covers that case.
 */
const LATEST_CONFIG_DESCRIPTION =
  'Schedule a GenPT test using the latest configuration. Review the setup and ensure required credentials are valid and active.';

/** Debounce before POST `/genpt/validate_target` when the resolved URL changes (ROAR-4361). */
const SCHEDULE_MODAL_VALIDATE_URL_DEBOUNCE_MS = 400;

/**
 * Reusable modal: Configuration → Throttling → Schedule, then POST /genpt/schedules only
 * (no /genpt/test start). For **new** web and **API** schedules, `/genpt/validate_target` supplies
 * `input_target_url`, `validated_target_url`, `crm_type`, `latest_session`, etc. (same as chat where
 * applicable). For **web edit**, GET `test_config` may include `asset_type` and optional `crm_type`
 * (WordPress) — when present we skip validate_target and seed forms from that.
 */
function ScheduleGenPTModal({
  open,
  onClose,
  /** Target URL for the asset (when not using pickAssetFirst) */
  targetURL,
  assetId = null,
  /** Display name shown in title (e.g. asset friendly name) */
  assetDisplayName = '',
  /** e.g. web_app | api — matches AllAssets / API constants */
  platformCategory = WEB_APP,
  /** Pass through for internal/external VPN & auth (e.g. from asset row `exposure` / `mode`) */
  assetExposure = null,
  /**
   * When true (e.g. GenPT overview), first step is asset search; then test setup → throttling → schedule.
   * When false, `targetURL` + `assetId` must be provided (e.g. All Assets row).
   */
  pickAssetFirst = false,
  /** Optional company id for “Create new asset”; falls back to Storage */
  companyId: companyIdProp = null,
  /** Full schedule object from GET /genpt/schedules/:id — opens same wizard prefilled for edit */
  scheduleToEdit = null,
  /** Optional name from All Schedules row if GET payload omits schedule title */
  scheduleNameHint = null,
  /** All Schedules table: modal opens before GET completes; show in-modal loader until false */
  editScheduleDetailLoading = false,
  /**
   * Where the modal is being opened from. Defaults to `'UI'` (standard product surfaces such as
   * All Assets / GenPT overview / All Schedules). CI/CD Configuration passes `'pipeline'` so the
   * same wizard can be reused without altering existing UI behavior.
   */
  source = 'UI',
  modalName = '',
  modalDescription = MODAL_DESCRIPTION,
  /**
   * CI/CD Configuration (`source="pipeline"`): called when the user finishes the API collection
   * step so the parent can merge `apiCollectionURL` and `schemaEndpointPath` into pipeline state.
   */
  onPipelineCollectionConfigured = null,
  /**
   * CI/CD pipeline + web asset: persist test + throttling from TestConfigurationForm (no schedule step);
   * parent should PATCH CI/CD config, update local state, and close the modal.
   */
  onPipelineWebSave = null,
  /**
   * CI/CD (`source="pipeline"`): current pipeline state so Configuration + Throttling match what was
   * saved for this API key (GET hydrate or last modal save). Ignored when `scheduleToEdit` is set.
   */
  pipelineConfigPrefill = null,
}) {
  const [popup] = useContext(AlertContext);
  const { data: loginData } = useValidateLogin();
  const { refreshTrialStats } = useAppMode();
  const queryClient = useQueryClient();

  const createGenptSchedule = useCreateGenPTSchedule();
  const updateGenptSchedule = useUpdateGenPTSchedule();
  const validateTargetMutation = useValidateTarget();
  /** RQ v3: whole `useMutation` return is not referentially stable — never put it in effect deps or validate_target loops forever. */
  const validateTargetMutateAsyncRef = useRef(validateTargetMutation.mutateAsync);
  validateTargetMutateAsyncRef.current = validateTargetMutation.mutateAsync;
  const { mainUrl = '' } = useContext(LayoutContext) || {};
  const navigate = useNavigate();

  const [starting, setStarting] = useState(false);
  /** 'asset' = search & pick; 'configure' = test setup → throttle → schedule */
  const [phase, setPhase] = useState('configure');
  const [picked, setPicked] = useState(null);
  const [changeAssetConfirmOpen, setChangeAssetConfirmOpen] = useState(false);
  const [discardEditConfirmOpen, setDiscardEditConfirmOpen] = useState(false);
  /** After successful create/update: show in-modal success (design) instead of closing immediately. */
  const [scheduleSuccessKind, setScheduleSuccessKind] = useState(null);

  const companyId =
    companyIdProp ??
    loginData?.user?.company_id ??
    loginData?.user?.currentCompanyId ??
    null;

  useEffect(() => {
    if (!open) {
      setPhase('configure');
      setPicked(null);
      setChangeAssetConfirmOpen(false);
      setDiscardEditConfirmOpen(false);
      setScheduleSuccessKind(null);
      return;
    }
    if (scheduleToEdit) {
      setPhase('configure');
      return;
    }
    if (pickAssetFirst) {
      setPhase('asset');
      setPicked(null);
    } else {
      setPhase('configure');
      setPicked(null);
    }
  }, [open, pickAssetFirst, scheduleToEdit]);

  const tcFromEdit = scheduleToEdit?.test_config || scheduleToEdit?.testConfig;

  const targetURLResolved = scheduleToEdit
    ? tcFromEdit?.targetURL ??
      tcFromEdit?.target_url ??
      scheduleToEdit.url ??
      scheduleToEdit.target_url ??
      picked?.targetURL ??
      targetURL
    : picked?.targetURL ?? targetURL;

  const assetIdResolved = scheduleToEdit
    ? scheduleToEdit.assetId ?? scheduleToEdit.asset_id ?? picked?.assetId ?? assetId
    : picked?.assetId ?? assetId;

  const assetDisplayNameResolved = scheduleToEdit
    ? scheduleToEdit.assetName ??
      scheduleToEdit.asset_name ??
      scheduleToEdit.asset?.name ??
      picked?.assetDisplayName ??
      assetDisplayName
    : picked?.assetDisplayName ?? assetDisplayName;

  const platformCategoryResolved = scheduleToEdit
    ? scheduleToEdit.platform_category ??
      scheduleToEdit.platformCategory ??
      picked?.platformCategory ??
      platformCategory
    : picked?.platformCategory ?? platformCategory;

  const assetExposureResolved = picked?.assetExposure ?? assetExposure;

  const scheduleEditPrefill = useMemo(() => {
    if (!scheduleToEdit || editScheduleDetailLoading) return null;
    return mapGenptScheduleToEditPrefill(scheduleToEdit, { scheduleNameHint });
  }, [scheduleToEdit, scheduleNameHint, editScheduleDetailLoading]);

  const pipelineEditPrefill = useMemo(() => {
    if (source !== 'pipeline' || scheduleToEdit || !pipelineConfigPrefill) return null;
    return mapPipelineConfigToGenptEditPrefill(pipelineConfigPrefill, {
      targetURL: targetURLResolved,
      assetId: assetIdResolved,
      assetExposure: assetExposureResolved,
    });
  }, [
    source,
    scheduleToEdit,
    pipelineConfigPrefill,
    targetURLResolved,
    assetIdResolved,
    assetExposureResolved,
  ]);

  const formEditPrefill = scheduleEditPrefill ?? pipelineEditPrefill;

  const editScheduleId =
    scheduleToEdit?.scheduleId ??
    scheduleToEdit?.schedule_id ??
    scheduleToEdit?.id ??
    scheduleToEdit?.schedule?.id ??
    scheduleToEdit?.schedule?.scheduleId ??
    scheduleToEdit?.schedule?.schedule_id ??
    null;

  const isApi = platformCategoryResolved === API;

  /**
   * ROAR-4328: In this modal, "Confirm and Save" when asset is already fixed (no pick-asset step) or when
   * editing a schedule; pick-asset-first new schedule stays "Schedule Test". Chat / in-page wizards pass
   * `embeddedInModal={false}` and always get "Confirm and Save" on the schedule step from the form.
   */
  const scheduleSubmitConfirmAndSave = Boolean(scheduleToEdit) || !pickAssetFirst;

  const resetOnClose = useCallback(() => {
    setStarting(false);
  }, []);

  /** Target summary + active-schedule banner: pick-asset flow *or* direct open (All Assets / Explore). */
  const showTargetAssetSummary = Boolean(
    open &&
      phase === 'configure' &&
      assetIdResolved != null &&
      targetURLResolved &&
      (pickAssetFirst ? picked != null : true)
  );

  const { data: schedulesListData, isSuccess: schedulesLoaded } = useGenptSchedulesList(
    {
      assetId: assetIdResolved,
      page: 1,
      pageSize: 50,
      status: 'active',
    },
    { enabled: showTargetAssetSummary }
  );

  const activeSchedulesCount = useMemo(() => {
    if (!schedulesLoaded || schedulesListData == null) return null;
    return getGenptSchedulesTotalCount(schedulesListData);
  }, [schedulesLoaded, schedulesListData]);

  const goToSchedulesForAsset = useCallback(() => {
    const id = assetIdResolved;
    if (id == null) return;
    onClose();
    resetOnClose();
    const urlForSearch = (targetURLResolved && String(targetURLResolved).trim()) || '';
    const name = (assetDisplayNameResolved || picked?.displayPrimary || '').trim();
    const searchVal = urlForSearch.length > 0 ? urlForSearch : name;
    const params = new URLSearchParams();
    params.set('assetId', String(id));
    if (searchVal.length > 0) params.set('scheduleSearch', searchVal);
    params.set('scheduleStatus', 'active');
    navigate(`${mainUrl}/genpt/schedules?${params.toString()}`);
  }, [
    assetDisplayNameResolved,
    assetIdResolved,
    mainUrl,
    navigate,
    onClose,
    picked?.displayPrimary,
    resetOnClose,
    targetURLResolved,
  ]);

  /**
   * Edit + live validate: minimal CRM hint merged under live response (ROAR-4361) when we still
   * call `/genpt/validate_target` (e.g. legacy payloads without `asset_type` on `test_config`).
   */
  const editValidateTargetHint = useMemo(() => {
    if (!scheduleToEdit || isApi) return {};
    const tc = tcFromEdit;
    if (!tc || typeof tc !== 'object') return {};
    const crm = tc?.crm_type ?? tc?.crmType;
    const tt = String(tc?.test_type ?? tc?.testType ?? '').trim().toLowerCase();
    if (crm != null && String(crm).trim() !== '') {
      return { crm_type: String(crm).toLowerCase() };
    }
    if (tt === 'wordpress') return { crm_type: 'wordpress' };
    return {};
  }, [scheduleToEdit, isApi, tcFromEdit]);

  /**
   * When GET schedule includes `test_config.asset_type`, we can seed validate-target-shaped data
   * for the configure step without calling `/genpt/validate_target` (crm_type only for WordPress).
   */
  const editScheduleValidateTargetBaseline = useMemo(() => {
    if (!scheduleToEdit || isApi) return null;
    const tc = tcFromEdit;
    if (!tc || typeof tc !== 'object') return null;
    const assetType =
      tc.asset_type ??
      tc.assetType ??
      scheduleToEdit.platform_category ??
      scheduleToEdit.platformCategory;
    if (assetType == null || String(assetType).trim() === '') return null;

    const tt = String(tc.test_type ?? tc.testType ?? '').trim().toLowerCase();
    const crmRaw = tc.crm_type ?? tc.crmType;
    let crm_type =
      crmRaw != null && String(crmRaw).trim() !== ''
        ? String(crmRaw).trim().toLowerCase()
        : null;
    if (!crm_type && tt === 'wordpress') crm_type = 'wordpress';

    const out = {
      success: true,
      asset_type: String(assetType).trim().toLowerCase(),
    };
    if (tt) {
      out.test_type = tt;
      out.testType = tt;
    }
    if (crm_type) out.crm_type = crm_type;
    return out;
  }, [scheduleToEdit, isApi, tcFromEdit]);

  const skipValidateTargetForWebEdit = editScheduleValidateTargetBaseline != null;

  const [scheduleValidateTargetData, setScheduleValidateTargetData] = useState({});
  const [debouncedTargetUrlForValidate, setDebouncedTargetUrlForValidate] = useState('');
  const validateUrlSeqRef = useRef(0);

  /** Do not mount configure forms until validate_target settles (or web edit baseline skips the call). */
  const [scheduleTargetValidateReady, setScheduleTargetValidateReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setScheduleValidateTargetData({});
      setDebouncedTargetUrlForValidate('');
      setScheduleTargetValidateReady(false);
      validateUrlSeqRef.current += 1;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const raw = String(targetURLResolved || '').trim();
    const id = setTimeout(() => {
      setDebouncedTargetUrlForValidate(raw);
    }, SCHEDULE_MODAL_VALIDATE_URL_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [open, targetURLResolved]);

  useEffect(() => {
    if (!open) return;
    if (skipValidateTargetForWebEdit) {
      setScheduleTargetValidateReady(true);
      return;
    }
    if (companyId == null) {
      setScheduleTargetValidateReady(true);
      return;
    }
    if (!debouncedTargetUrlForValidate) {
      setScheduleTargetValidateReady(false);
      return;
    }
    setScheduleTargetValidateReady(false);
  }, [open, companyId, debouncedTargetUrlForValidate, skipValidateTargetForWebEdit]);

  useEffect(() => {
    if (!open) return;
    if (skipValidateTargetForWebEdit) {
      setScheduleValidateTargetData({});
      setScheduleTargetValidateReady(true);
      return;
    }
    if (!debouncedTargetUrlForValidate || companyId == null) {
      return;
    }
    const seq = ++validateUrlSeqRef.current;
    let cancelled = false;
    (async () => {
      try {
        const payload = {
          targetURL: debouncedTargetUrlForValidate,
          companyId,
          networkAccess: assetExposureResolved === 'internal' ? 'internal' : 'external',
          forScheduling: true,
        };
        if (assetIdResolved != null) payload.assetId = assetIdResolved;
        const res = await validateTargetMutateAsyncRef.current(payload);
        if (cancelled || seq !== validateUrlSeqRef.current) return;
        if (res && typeof res === 'object') {
          setScheduleValidateTargetData(res);
        } else {
          setScheduleValidateTargetData({});
        }
        setScheduleTargetValidateReady(true);
      } catch {
        if (cancelled || seq !== validateUrlSeqRef.current) return;
        setScheduleValidateTargetData({});
        setScheduleTargetValidateReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    debouncedTargetUrlForValidate,
    companyId,
    assetExposureResolved,
    assetIdResolved,
    skipValidateTargetForWebEdit,
  ]);

  /**
   * ROAR-4361: Live `/genpt/validate_target` is spread after edit seed (baseline or hint). Preserve
   * seed `crm_type` when live omits it; infer WordPress from live `test_type` when `crm_type` absent.
   */
  const mergedScheduleValidateTargetData = useMemo(() => {
    const live =
      scheduleValidateTargetData && typeof scheduleValidateTargetData === 'object'
        ? scheduleValidateTargetData
        : {};
    const seed =
      editScheduleValidateTargetBaseline && typeof editScheduleValidateTargetBaseline === 'object'
        ? editScheduleValidateTargetBaseline
        : editValidateTargetHint && typeof editValidateTargetHint === 'object'
          ? editValidateTargetHint
          : {};
    const merged = { ...seed, ...live };
    const norm = (v) => {
      if (v == null) return null;
      const s = String(v).trim().toLowerCase();
      return s || null;
    };
    const liveCrm = norm(live.crm_type);
    const seedCrm = norm(seed.crm_type);
    const liveWpFromType =
      String(live.test_type ?? live.testType ?? '').trim().toLowerCase() === 'wordpress';
    let crm = liveCrm || (liveWpFromType ? 'wordpress' : null) || seedCrm;
    if (crm) merged.crm_type = crm;
    else delete merged.crm_type;
    return merged;
  }, [editScheduleValidateTargetBaseline, editValidateTargetHint, scheduleValidateTargetData]);

  const showConfigureStep =
    open &&
    phase === 'configure' &&
    targetURLResolved &&
    (!pickAssetFirst || picked || scheduleToEdit);

  const latestSessionForSynthetic = useMemo(() => {
    const ls =
      mergedScheduleValidateTargetData?.latest_session ??
      mergedScheduleValidateTargetData?.latestSession ??
      null;
    return ls && typeof ls === 'object' ? ls : null;
  }, [mergedScheduleValidateTargetData]);

  const syntheticMsg = useMemo(() => {
    const inputTarget = String(
      mergedScheduleValidateTargetData?.input_target_url ||
        mergedScheduleValidateTargetData?.inputTargetUrl ||
        ''
    ).trim();
    const validatedTu = String(
      mergedScheduleValidateTargetData?.validated_target_url ||
        mergedScheduleValidateTargetData?.validatedTargetUrl ||
        ''
    ).trim();
    return {
      metadata: {
        asset: {
          id: assetIdResolved,
          ...(assetExposureResolved != null && {
            network_access: assetExposureResolved === 'internal' ? 'internal' : 'external',
            mode: assetExposureResolved,
          }),
        },
        ...(targetURLResolved && {
          target_url: targetURLResolved,
          targetURL: targetURLResolved,
        }),
        ...(inputTarget && { input_target_url: inputTarget }),
        ...(validatedTu && { validated_target_url: validatedTu }),
        ...(latestSessionForSynthetic && { latest_session: latestSessionForSynthetic }),
      },
    };
  }, [
    assetIdResolved,
    assetExposureResolved,
    targetURLResolved,
    latestSessionForSynthetic,
    mergedScheduleValidateTargetData,
  ]);

  const effectiveTargetURL = showConfigureStep ? targetURLResolved : null;

  const handleConfigCompleted = async (configData) => {
    const resolvedAssetId = configData.assetId ?? assetIdResolved ?? null;
    if (resolvedAssetId == null) {
      popup({
        title: 'Error',
        variant: 'error',
        description: 'Asset ID is required to create a schedule.',
        okayText: 'Okay',
      });
      return;
    }

    if (!configData?.schedule) {
      if (source === 'pipeline') {
        return;
      }
      popup({
        title: 'Error',
        variant: 'error',
        description: 'Schedule details are missing.',
        okayText: 'Okay',
      });
      return;
    }

    if (scheduleToEdit && editScheduleId == null) {
      popup({
        title: 'Error',
        variant: 'error',
        description: 'Could not determine which schedule to update. Close and open Edit again.',
        okayText: 'Okay',
      });
      return;
    }

    const isEdit = Boolean(scheduleToEdit && editScheduleId != null);
    // validate_target infers asset_type from the URL and often returns web_app for API inventory rows;
    // schedule platform comes from the picked asset / modal — prefer that for API (ROAR-4446 follow-up).
    const resolvedAssetType =
      configData.asset_type ??
      configData.assetType ??
      (platformCategoryResolved === API
        ? API
        : mergedScheduleValidateTargetData?.asset_type ?? WEB_APP);

    const internalPayload = buildGenptScheduleApiPayload({
      scheduleUi: configData.schedule,
      testConfigData: {
        ...configData,
        assetId: resolvedAssetId,
        targetURL: configData.targetURL,
        assetType: resolvedAssetType,
      },
      configSettings: configData.config_settings || null,
      assetId: resolvedAssetId,
      companyId,
      forSchedulesEndpointOnly: true,
      /** PUT: explicit `null` on the sibling collection field when switching URL ↔ file (backend unset). */
      clearSiblingCollectionFieldsAsNull: isEdit,
    });
    let putBody = buildGenptScheduleUpdatePutBody(internalPayload);

    if (isEdit && scheduleToEdit) {
      const baselinePrefill =
        scheduleEditPrefill ?? mapGenptScheduleToEditPrefill(scheduleToEdit, { scheduleNameHint });
      if (baselinePrefill) {
        const baselineAssetId = baselinePrefill.submittedData.assetId ?? resolvedAssetId;
        const baselineTarget =
          baselinePrefill.submittedData.targetURL ?? targetURLResolved ?? configData.targetURL ?? '';
        const baselineInternal = buildGenptScheduleApiPayload({
          scheduleUi: baselinePrefill.submittedData.schedule,
          testConfigData: {
            ...baselinePrefill.submittedData,
            assetId: baselineAssetId,
            targetURL: baselineTarget,
          },
          configSettings: baselinePrefill.throttlingData,
          assetId: baselineAssetId,
          companyId,
          forSchedulesEndpointOnly: true,
        });
        putBody = pickChangedSchedulePutFields(putBody, buildGenptScheduleUpdatePutBody(baselineInternal));
      }
    }

    if (isEdit && Object.keys(putBody).length === 0) {
      popup({
        title: 'No changes',
        variant: 'success',
        description:
          'Nothing differs from the saved schedule — test configuration, throttling, timing, and recurrence are all unchanged.',
        okayText: 'Okay',
      });
      onClose();
      resetOnClose();
      return;
    }

    const slotConflict = await hasGenptScheduleSlotConflict({
      assetId: resolvedAssetId,
      scheduleUi: configData.schedule,
      excludeScheduleId: isEdit ? editScheduleId : null,
    });
    if (slotConflict) {
      popup({
        title: 'Error',
        variant: 'error',
        description:
          'This asset is already scheduled for a test at the selected date and time. Please choose a different slot.',
        okayText: 'Okay',
      });
      return;
    }

    setStarting(true);
    try {
      const createBody = buildGenptScheduleCreateBody(internalPayload);
      const res = isEdit
        ? await updateGenptSchedule.mutateAsync({
            scheduleId: editScheduleId,
            body: putBody,
          })
        : await createGenptSchedule.mutateAsync(createBody);

      const ok =
        res &&
        (res.success === true ||
          res.success === undefined ||
          res.id != null ||
          res.scheduleId != null);

      if (ok) {
        await refreshTrialStats?.();
        queryClient.invalidateQueries(['LIST_GENPT_SCHEDULES']);
        queryClient.invalidateQueries(['GENPT_SCHEDULES_LIST']);
        queryClient.invalidateQueries([GENPT_SCHEDULES_ACTIVE_TOTAL_QUERY_KEY]);
        if (isEdit && editScheduleId != null) {
          queryClient.invalidateQueries(['GENPT_SCHEDULE_BY_ID', String(editScheduleId)]);
        }
        setScheduleSuccessKind(isEdit ? 'updated' : 'created');
        return;
      }

      const failMsg =
        res?.message || (isEdit ? 'Could not update the schedule.' : 'Could not create the schedule.');
      popup({
        title: 'Failed',
        variant: 'error',
        description: failMsg,
        okayText: 'Okay',
      });
      const failed = new Error(failMsg);
      failed.scheduleErrorPopupShown = true;
      throw failed;
    } catch (err) {
      if (!err?.scheduleErrorPopupShown) {
        popup({
          title: 'Error',
          variant: 'error',
          description:
            err?.response?.data?.message ||
            err?.message ||
            (scheduleToEdit
              ? 'An error occurred while updating the schedule.'
              : 'An error occurred while creating the schedule.'),
          okayText: 'Okay',
        });
      }
      throw err;
    } finally {
      setStarting(false);
    }
  };

  const titleName =
    (editScheduleDetailLoading &&
      scheduleNameHint &&
      String(scheduleNameHint).trim()) ||
    assetDisplayNameResolved ||
    targetURLResolved ||
    targetURL ||
    'Asset';

  /**
   * ROAR-4316: Header copy must reflect what the user is about to do, not just the entry point.
   *  - Edit flow: existing edit copy.
   *  - Asset-pick step (pre-pick): keep the "select asset first" copy.
   *  - Configure step with prior test history (`latest_session` from /genpt/validate_target):
   *    surface the "using the latest configuration" copy (covers cases A & B in the ticket —
   *    history-only and history+schedules; the gray active-schedules banner stays additive).
   *  - Configure step with no prior history: default schedule copy (case C).
   */
  const hasLatestSession = Boolean(scheduleValidateTargetData?.latest_session);
  const headerDescription = scheduleToEdit
    ? EDIT_MODAL_DESCRIPTION
    : phase === 'asset'
      ? ASSET_STEP_DESCRIPTION
      : hasLatestSession
        ? LATEST_CONFIG_DESCRIPTION
        : modalDescription;

  /** Selected-asset card (pickAssetFirst configure step) — matches All Assets row + mock */
  const selectedCardPrimary = useMemo(() => {
    if (picked?.displayPrimary) return picked.displayPrimary;
    return (
      assetDisplayNameResolved ||
      (targetURLResolved && String(targetURLResolved).replace(/^https?:\/\//i, '')) ||
      'Asset'
    );
  }, [picked, assetDisplayNameResolved, targetURLResolved]);

  const selectedCardSecondary = useMemo(() => {
    if (picked?.displaySecondary) return picked.displaySecondary;
    const primary = (picked?.displayPrimary ?? assetDisplayNameResolved ?? '').trim();
    const url = targetURLResolved || '';
    const host = url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
    if (host && host !== primary) return host;
    const plat = platformMapping[platformCategoryResolved];
    const exposureLabel =
      assetExposureResolved === 'internal'
        ? 'Internal'
        : assetExposureResolved === 'external'
          ? 'External'
          : null;
    if (plat && exposureLabel) return `${plat} • ${exposureLabel}`;
    if (plat) return plat;
    return exposureLabel || '';
  }, [
    picked,
    assetDisplayNameResolved,
    targetURLResolved,
    platformCategoryResolved,
    assetExposureResolved,
  ]);

  const finalizeClose = useCallback(() => {
    onClose();
    resetOnClose();
  }, [onClose, resetOnClose]);

  const goToGenptSchedulesTab = useCallback(() => {
    finalizeClose();
    navigate(`${mainUrl}/genpt/schedules`);
  }, [finalizeClose, mainUrl, navigate]);

  /** Close / escape: edit flow requires discard confirmation. Backdrop clicks do not dismiss (avoid accidental close). */
  const handleDialogClose = useCallback((_event, reason) => {
    if (reason === 'backdropClick') return;
    if (starting) return;
    if (scheduleSuccessKind) {
      finalizeClose();
      return;
    }
    if (scheduleToEdit && !editScheduleDetailLoading) {
      setDiscardEditConfirmOpen(true);
      return;
    }
    finalizeClose();
  }, [starting, scheduleSuccessKind, scheduleToEdit, editScheduleDetailLoading, finalizeClose]);

  return (
    <>
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={scheduleSuccessKind ? false : 'md'}
      fullWidth={!scheduleSuccessKind}
      scroll="paper"
      disableEnforceFocus
      disableScrollLock
      sx={{
        /* theme.zIndex.modal (1300): above drawer (1200 in mui_overrides). Dropdowns use 1500 there. */
        zIndex: (theme) => theme.zIndex.modal,
      }}
      PaperProps={{
        className: '!rounded-xl !flex !flex-col !max-h-[90vh] !overflow-hidden',
        sx: {
          // Inner DialogContent scrolls; keep header visible (override MUI paper overflowY: auto)
          overflowY: 'hidden',
          ...(scheduleSuccessKind
            ? {
                /* Wide enough for success copy in ~2 lines; icon scales with GreenCheck class below */
                maxWidth: 'min(100%, 640px)',
                width: 'min(100%, 640px)',
              }
            : {
                maxWidth: 'min(100%, 720px)',
              }),
        },
      }}
    >
      <div className="relative shrink-0 px-6 pt-6 pb-2 border-b border-gray-100">
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          className="!absolute !right-2 !top-2"
          size="small"
          disabled={starting && !scheduleSuccessKind}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h5" className="!text-primary-500 !font-bold pr-10">
          {scheduleToEdit
            ? `Schedule GenPT Test — ${titleName}`
            : pickAssetFirst
              ? 'Schedule GenPT Test'
              : modalName ? modalName : `Schedule GenPT Test — ${titleName}`}
        </Typography>
        {!scheduleSuccessKind ? (
          <Typography variant="body2" className="!text-gray-500 !mt-2 !mb-1 max-w-3xl">
            {headerDescription}
          </Typography>
        ) : null}
      </div>

      <DialogContent className="!pt-4 !pb-6 !flex-1 !min-h-0 !overflow-y-auto">
        {scheduleSuccessKind ? (
          <div className="flex flex-col items-center justify-center px-6 py-8 text-center">
            <div className="mb-6 flex min-h-[132px] items-center justify-center" aria-hidden>
              <GreenCheck className="!h-32 !w-32 shrink-0" />
            </div>
            <Typography variant="h6" className="!font-bold !text-gray-900 !mb-3">
              {scheduleSuccessKind === 'updated'
                ? 'Schedule Successfully Updated'
                : 'Schedule Successfully Created'}
            </Typography>
            <Typography
              variant="body1"
              className="!text-gray-600 !leading-relaxed !max-w-full !mb-8 text-balance"
            >
              {scheduleSuccessKind === 'updated'
                ? `Your GenPT schedule for ${titleName} has been updated. You can manage or disable it anytime from the GenPT Schedules tab.`
                : `Your GenPT schedule for ${titleName} is set. You can manage or disable it anytime from the GenPT Schedules tab.`}
            </Typography>
            <Button
              type="button"
              variant="contained"
              onClick={goToGenptSchedulesTab}
              className="!uppercase !tracking-wide !text-xs !font-semibold !bg-primary-500 hover:!bg-primary-600 !text-white !px-8"
            >
              GenPT Schedules
            </Button>
          </div>
        ) : null}

        {!scheduleSuccessKind && editScheduleDetailLoading && (
          <div
            className="flex min-h-[200px] flex-col items-center justify-center gap-3 py-10 px-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label="Loading schedule details"
          >
            <CircularProgress size={40} thickness={4} sx={{ color: '#12AFB8' }} aria-hidden />
            <Typography variant="body2" className="!text-center !text-gray-600 !max-w-md">
              Loading schedule details…
            </Typography>
          </div>
        )}

        {!scheduleSuccessKind && open && pickAssetFirst && phase === 'asset' && (
          <ScheduleGenPTAssetStep
            companyId={companyIdProp}
            onSelectAsset={(sel) => {
              setPicked(sel);
              setPhase('configure');
            }}
          />
        )}

        {!scheduleSuccessKind && !editScheduleDetailLoading && effectiveTargetURL && (
          <div className={starting ? 'pointer-events-none opacity-70' : ''}>
            {showTargetAssetSummary && (
              <div className="mb-6 space-y-3">
                <div className="flex items-center gap-2">
                  <img
                    src={TARGET_ASSET_ICON_SRC}
                    alt=""
                    width={20}
                    height={20}
                    className="h-5 w-5 flex-shrink-0"
                    aria-hidden
                  />
                  <Typography variant="subtitle1" className="!font-semibold !text-gray-800">
                    Target Asset
                  </Typography>
                </div>
                <div className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <span className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center">
                    <span
                      className="absolute h-10 w-10 rounded-full bg-[#3ADA7A]/20"
                      aria-hidden
                    />
                    <CheckCircleIcon
                      className="relative z-[1]"
                      sx={{ fontSize: 28, color: '#3ADA7A' }}
                      aria-hidden
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <Typography
                      variant="subtitle1"
                      className="!font-semibold !text-gray-900 max-w-full truncate"
                      title={selectedCardPrimary}
                    >
                      {selectedCardPrimary}
                    </Typography>
                    {selectedCardSecondary ? (
                      <Typography
                        variant="body2"
                        className="!text-gray-500 max-w-full truncate !mt-0.5"
                      >
                        {selectedCardSecondary}
                      </Typography>
                    ) : null}
                  </div>
                  {pickAssetFirst ? (
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      className="!flex-shrink-0 !uppercase !text-xs !tracking-wide !min-w-[9rem] !border-primary-500 !text-primary-500 hover:!bg-primary-50"
                      onClick={() => setChangeAssetConfirmOpen(true)}
                    >
                      Change asset
                    </Button>
                  ) : null}
                </div>
                {activeSchedulesCount != null && activeSchedulesCount > 0 && (
                  <div className="flex gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                    <InfoOutlinedIcon className="!text-primary-500 flex-shrink-0 mt-0.5" fontSize="small" />
                    <Typography variant="body2" className="!text-gray-700 !leading-relaxed">
                      This asset currently has {activeSchedulesCount} active{' '}
                      {pluralize('schedule', activeSchedulesCount)}. You can review or manage them{' '}
                      <button
                        type="button"
                        className="inline p-0 border-0 bg-transparent text-primary-500 underline cursor-pointer text-[inherit] font-inherit hover:opacity-80"
                        onClick={goToSchedulesForAsset}
                      >
                        here
                      </button>
                      .
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {scheduleTargetValidateReady ? (
              isApi ? (
                <APIConfigurationForm
                  key={editScheduleId ? `edit-api-${editScheduleId}` : 'create-api'}
                  msg={syntheticMsg}
                  targetURL={effectiveTargetURL}
                  validateTargetData={mergedScheduleValidateTargetData}
                  onConfigCompleted={handleConfigCompleted}
                  suppressDeclarationEvent
                  embeddedInModal
                  scheduleSubmitConfirmAndSave={scheduleSubmitConfirmAndSave}
                  submittedDataProp={formEditPrefill?.submittedData ?? null}
                  onDeclarationDiscard={finalizeClose}
                  editingScheduleId={editScheduleId}
                  onPipelineCollectionConfigured={onPipelineCollectionConfigured}
                  hideScheduleTab={source === 'pipeline'}
                  onPipelineThrottleSave={
                    source === 'pipeline' && typeof onPipelineWebSave === 'function'
                      ? onPipelineWebSave
                      : null
                  }
                />
              ) : (
                <TestConfigurationForm
                  key={editScheduleId ? `edit-${editScheduleId}` : 'create-schedule'}
                  msg={syntheticMsg}
                  targetURL={effectiveTargetURL}
                  validateTargetData={mergedScheduleValidateTargetData}
                  onConfigCompleted={handleConfigCompleted}
                  suppressDeclarationEvent
                  embeddedInModal
                  forScheduling
                  scheduleSubmitConfirmAndSave={scheduleSubmitConfirmAndSave}
                  editPrefill={formEditPrefill}
                  onDeclarationDiscard={finalizeClose}
                  editingScheduleId={editScheduleId}
                  hideScheduleTab={source === 'pipeline'}
                  onPipelineThrottleSave={
                    source === 'pipeline' && typeof onPipelineWebSave === 'function'
                      ? onPipelineWebSave
                      : null
                  }
                />
              )
            ) : (
              <div className="flex flex-col items-center justify-center gap-4 py-14 px-4">
                <MainLoader />
                <Typography variant="body2" className="!text-center !text-gray-600 !max-w-md">
                  {isApi
                    ? 'Validating this API asset and preparing configuration…'
                    : 'Detecting site type and preparing the right test options for this URL…'}
                </Typography>
              </div>
            )}
          </div>
        )}

        {!scheduleSuccessKind && starting && (
          <div className="flex justify-center py-6">
            <MainLoader />
          </div>
        )}
      </DialogContent>
    </Dialog>
    <Modal
      open={discardEditConfirmOpen}
      maxWidth={false}
      fullWidth={false}
      sx={{
        zIndex: (theme) => theme.zIndex.tooltip + 100,
      }}
      PaperProps={{
        className: '!rounded-xl !text-left',
        sx: {
          width: '100%',
          maxWidth: 'min(520px, calc(100vw - 32px))',
          boxSizing: 'border-box',
          margin: '16px',
        },
      }}
    >
      <div className="text-left">
        <Typography variant="h6" className="!font-semibold !text-gray-900 !mb-3">
          Discard changes?
        </Typography>
        <Typography variant="body1" className="!font-medium !text-gray-600 !leading-relaxed">
          If you close this now, your recent changes will be discarded. The existing schedule will remain
          unchanged.
        </Typography>
        <Divider className="!my-5" />
        <div className="flex flex-nowrap items-center justify-center gap-2 sm:gap-3">
          <Button
            type="button"
            variant="contained"
            size="extra_small"
            onClick={() => setDiscardEditConfirmOpen(false)}
            className="!shrink-0 !whitespace-nowrap !uppercase !tracking-wide !text-xs !font-semibold !bg-primary-500 hover:!bg-primary-600 !text-white !px-4"
          >
            Keep editing
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="extra_small"
            onClick={() => {
              setDiscardEditConfirmOpen(false);
              finalizeClose();
            }}
            className="!shrink-0 !whitespace-nowrap !uppercase !tracking-wide !text-xs !font-semibold !border-primary-500 !text-primary-500 !bg-white hover:!bg-primary-50 !px-4"
          >
            Discard changes
          </Button>
        </div>
      </div>
    </Modal>
    <Modal
      open={changeAssetConfirmOpen}
      onClose={() => setChangeAssetConfirmOpen(false)}
      maxWidth="sm"
      sx={{
        zIndex: (theme) => theme.zIndex.tooltip + 100,
      }}
      PaperProps={{ className: '!min-w-[300px]' }}
    >
      <div className="text-left">
        <div className='p-1'>
        <Typography variant="h6" className="!font-semibold !text-gray-900 !mb-2">
          Change Target Asset?
        </Typography>
        <Typography variant="body1" className="text-gray-700">
          This action will remove all current test configurations and scheduling details. You will need to
          set up the test again for the selected asset.
        </Typography>
        </div>
        <Divider className="!my-4" />
        <div className="flex flex-col lg:flex-row justify-center gap-3">
          <Button
            variant="contained"
            onClick={() => {
              if (source === 'pipeline') {
                onClose();
              } else {
                setChangeAssetConfirmOpen(false)
              }
            }}
            className="!bg-primary-500 hover:!bg-primary-600 !text-white"
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setChangeAssetConfirmOpen(false);
              setPhase('asset');
              setPicked(null);
            }}
            className="!text-primary-500 !border-primary-500 !bg-white"
          >
            Change asset
          </Button>
        </div>
      </div>
    </Modal>
    </>
  );
}

/** Match Header/Menu.js MENU_ROW_ICON_SLOT for All Assets START dropdown alignment */
const SCHEDULE_MENU_ICON_SLOT = 'flex h-8 w-10 shrink-0 items-center justify-center';

/**
 * Dropdown row styled like Start New GenPT — opens subscribe modal if GenPT not enabled.
 */
export function ScheduleGenPTDropdownItem({
  label = 'Schedule GenPT',
  isDisabled: isDisabledProp = false,
  onScheduleClick,
  className = '',
  showIcon = true,
  from = 'all_assets',
  /** When true, show calendar asset icon instead of GenPT logo (e.g. All Assets Start test menu). */
  useCalendarIcon = false,
}) {
  const [colorChange, setColorChange] = useState(false);
  const { pathname } = useLocation();
  const isAdmin = pathname?.startsWith('/admin');
  const isDashboard = pathname?.startsWith('/app') || isAdmin;
  const genptEnabled = Storage.decryptStorageData('genpt_enabled') === 'true';
  const dispatch = useDispatch();
  const { data: loginData } = useValidateLogin();

  // ROAR-5007 (AC11): without genpt_scheduling in the plan the row is disabled with the lock icon +
  // upgrade hover, rather than opening a schedule wizard whose POST the API would reject anyway.
  // Lives here (not at the call sites) so every Start menu — All Assets, Asset detail — locks alike.
  const planLocked = isFeatureLocked(loginData?.user?.features, FEATURE_KEYS.GENPT_SCHEDULING);
  const isDisabled = isDisabledProp || planLocked;

  const handleClick = () => {
    if (isDisabled) return;
    if (genptEnabled) {
      onScheduleClick?.();
    } else {
      dispatch(setGenptSubscribeModal({ open: true, from }));
    }
  };

  return (
    <>
      {isDashboard && (
        <div
          className={`flex w-full items-center gap-2 ${
            isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
          }`}
          onClick={handleClick}
          onMouseEnter={!isDisabled ? () => setColorChange(true) : undefined}
          onMouseLeave={!isDisabled ? () => setColorChange(false) : undefined}
        >
          {showIcon && (
            <div className={SCHEDULE_MENU_ICON_SLOT}>
              {useCalendarIcon ? (
                <img
                  src="/images/genpt/calendar.svg"
                  alt=""
                  className="h-5 w-5 object-contain transition-[filter,opacity] duration-150"
                  style={{
                    filter: isDisabled
                      ? 'grayscale(1) opacity(0.55)'
                      : colorChange
                        ? 'none'
                        : 'brightness(0) saturate(100%) invert(48%) sepia(6%) saturate(200%) hue-rotate(176deg) brightness(96%) contrast(90%)',
                  }}
                  aria-hidden
                />
              ) : isDisabled ? (
                <SiembaGPTIconGrey />
              ) : colorChange ? (
                <SiembaGPTIconTheme />
              ) : (
                <SiembaGPTIconGrey />
              )}
            </div>
          )}
          <Button
            size="small"
            className={`bg-transparent !m-0 !min-h-0 !p-0 !normal-case ${
              isDisabled ? 'text-grey cursor-not-allowed' : colorChange ? 'primary-color' : 'text-grey'
            } ${className}`}
          >
            {label}
          </Button>
          {planLocked && <FeatureLockIcon testId="schedule-genpt-lock" />}
        </div>
      )}
    </>
  );
}

export default ScheduleGenPTModal;
